/**
 * Shows a one-time password immediately after it is issued by a user creation or password reset.
 *
 * This exists because passwords are no longer predictable. Every new user and every reset used to
 * receive the same source-visible `Welcome123!`, so the operator never had to be told what it was
 * — the previous reset dialog simply printed it in its own copy. Passwords are now generated per
 * account from `OsRng` and only their Argon2 hash is stored, so this dialog is the single
 * opportunity to convey one.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { KeyRound, Copy, Check } from 'lucide-react'

/** How long the copy button shows its confirmed state, in milliseconds. */
const COPY_FEEDBACK_MS = 2000

interface IssuedPasswordModalProps {
  password: string
  onClose: () => void
}

export function IssuedPasswordModal({
  password,
  onClose,
}: IssuedPasswordModalProps): React.ReactElement {
  const { t } = useTranslation('admin')
  const [copied, setCopied] = useState(false)

  const copy = (): void => {
    void navigator.clipboard.writeText(password).then(() => {
      setCopied(true)
      setTimeout(() => {
        setCopied(false)
      }, COPY_FEEDBACK_MS)
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl dark:bg-stone-800">
        <div className="p-6">
          <div className="mb-4 flex items-center gap-4">
            <div className="rounded-full bg-green-100 p-3 dark:bg-green-900/30">
              <KeyRound className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-stone-900 dark:text-stone-100">
                {t('users.issuedPassword.title')}
              </h2>
              <p className="text-sm text-stone-500 dark:text-stone-400">
                {t('users.issuedPassword.subtitle')}
              </p>
            </div>
          </div>

          <div className="mb-4 flex items-center gap-2 rounded-lg bg-stone-100 p-3 dark:bg-stone-700">
            <code className="flex-1 font-mono text-sm break-all text-stone-900 dark:text-stone-100">
              {password}
            </code>
            <button
              onClick={copy}
              aria-label={t('users.issuedPassword.copy')}
              className="rounded p-2 text-stone-500 hover:bg-stone-200 dark:hover:bg-stone-600"
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </button>
          </div>

          <div className="mb-6 rounded-lg bg-amber-50 p-3 dark:bg-amber-900/20">
            <p className="text-sm text-amber-700 dark:text-amber-300">
              {t('users.issuedPassword.warning')}
            </p>
          </div>

          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="rounded-lg bg-stone-800 px-4 py-2 text-white hover:bg-stone-900 dark:bg-stone-600 dark:hover:bg-stone-500"
            >
              {t('users.issuedPassword.done')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
